import Button from "kustom-front/src/components/atoms/Button";
import React, { FunctionComponent, useEffect, useState } from "react";
import { FormattedMessage, FormattedNumber, useIntl } from "react-intl";
import CartTooltip from "@Components/atoms/cart/CartTooltip";
import CartCGU from "@Components/molecules/cart/CartCGU";
import CartOverviewTile from "@Components/molecules/cart/CartOverviewTile";
import CartAddressContent from "@Components/molecules/cart/CartOverviewTile/CartAddressContent";
import CartDeliveryContent from "@Components/molecules/cart/CartOverviewTile/CartDeliveryContent";
import CartDiscountContent from "@Components/molecules/cart/CartOverviewTile/CartDiscountContent";
import CartOrderContent from "@Components/molecules/cart/CartOverviewTile/CartOrderContent";
import CartPaymentContent from "@Components/molecules/cart/CartOverviewTile/CartPaymentContent";
import useCartContext from "@Hooks/useCartContext";
import { BillingAddress } from "@Types/addresses";
import { Price } from "@Types/api/price";
import { Step } from "@Types/cart";
import { PaymentMethods } from "@Types/cart";
import { convertToCheckoutStepProperties, setDataLayerTrack } from "@Utils/track";
import useNotificationsContext from "../../../../../hooks/useNotificationsContext";
import CreditCard from "../../../../../types/creditCard";
import CartButtonSpinner from "../../../../atoms/cart/CartButtonSpinner";
import "./style.scss";

interface Props {
  availablePayments: PaymentMethods[];
  billing?: BillingAddress;
  CGUChecked: boolean;
  CGUError: boolean;
  displayDiscount: boolean;
  displayProcessout: boolean;
  checkMessage: string;
  isPaying: boolean;
  onChangeAddressContent: () => void;
  onChangeCreditCard: () => void;
  onChangeOrderContent: () => void;
  onChangePaymentMethod: (method: PaymentMethods) => void;
  onCheckCGU: () => void;
  onCompleteOrder: () => void;
  onSaveCreditCard: () => void;
  onUpdatePromoCode: (promoCode: string | undefined) => void;
  promoCodeErrorMessage: string | undefined;
  saveCreditCard: boolean;
  savedCreditCard?: CreditCard;
  selectedMethod: PaymentMethods;
  total: Price;
}

interface ContainerProps {
  onChangeCreditCard: () => void;
  onCompleteOrder: () => void;
  onSaveCreditCard: () => void;
  onChangeStep: (step: Step) => void;
  onUpdatePromoCode: (promoCode: string | undefined) => void;
  promoCodeErrorMessage: string | undefined;
}

const CartOverviewStep: FunctionComponent<Props> = ({
  availablePayments,
  billing,
  CGUChecked,
  CGUError,
  displayDiscount,
  displayProcessout,
  checkMessage,
  isPaying,
  onChangeAddressContent,
  onChangeCreditCard,
  onChangeOrderContent,
  onChangePaymentMethod,
  onCheckCGU,
  onCompleteOrder,
  onSaveCreditCard,
  onUpdatePromoCode,
  promoCodeErrorMessage,
  saveCreditCard,
  savedCreditCard,
  selectedMethod,
  total,
}) => {
  return (
    <div className="CartOverviewStep">
      <div className="CartOverviewStep-content-line">
        <CartOverviewTile onClick={onChangeOrderContent} type="order">
          <CartOrderContent />
        </CartOverviewTile>
      </div>
      <div className="CartOverviewStep-content-line">
        <CartAddressContent type="delivery" onClick={onChangeAddressContent} />
      </div>
      {billing && billing.firstName !== undefined && (
        <div className="CartOverviewStep-content-line">
          <CartAddressContent type="billing" onClick={onChangeAddressContent} />
        </div>
      )}
      {displayDiscount && (
        <div className="CartOverviewStep-content-line">
          <CartOverviewTile type="discount">
            <CartDiscountContent
              onUpdatePromoCode={onUpdatePromoCode}
              promoCodeErrorMessage={promoCodeErrorMessage}
            />
          </CartOverviewTile>
        </div>
      )}
      <div className="CartOverviewStep-content-line">
        <CartOverviewTile type="delivery">
          <CartDeliveryContent />
        </CartOverviewTile>
      </div>
      {total.cents > 0 ? (
        <div className="CartOverviewStep-content-line">
          <CartOverviewTile type="payment">
            <CartPaymentContent
              displayProcessout={displayProcessout}
              availablePayments={availablePayments}
              onChangeCreditCard={onChangeCreditCard}
              onChangePaymentMethod={onChangePaymentMethod}
              onSaveCreditCard={onSaveCreditCard}
              selectedMethod={selectedMethod}
              savedCreditCard={savedCreditCard}
              saveCreditCard={saveCreditCard}
            />
          </CartOverviewTile>
        </div>
      ) : (
        <></>
      )}

      {CGUError && (
        <div className="CartOverviewStep-content-line CartOverviewStep-error">
          <FormattedMessage id="cheerz_CartOverviewStep_CGU_error" />
        </div>
      )}

      <div className="CartOverviewStep-cgu-line">
        <CartCGU onCheck={onCheckCGU} checked={CGUChecked} />
      </div>

      <div className="CartOverviewStep-content-line CartOverviewStep-button-container">
        {total.cents > 0 ? (
          <Button type="primary" onClick={onCompleteOrder} disabled={isPaying}>
            {isPaying ? (
              <CartButtonSpinner />
            ) : (
              <>
                <span>
                  <FormattedMessage id="cheerz_CartOverviewStep_pay" />
                </span>
                <span>
                  <FormattedNumber
                    value={total.cents / 100}
                    style="currency"
                    currency={total.currency}
                  />
                </span>
              </>
            )}
            <CartTooltip info={checkMessage} />
          </Button>
        ) : (
          <Button type="accent" onClick={onCompleteOrder}>
            <FormattedMessage id="cheerz_CartOverviewStep_free" />
            <CartTooltip info={checkMessage} />
          </Button>
        )}
      </div>

      {total.cents > 0 && (
        <div className="CartOverviewStep-secure">
          <FormattedMessage id="cheerz_CartOverviewStep_secure" />
        </div>
      )}
    </div>
  );
};

export { CartOverviewStep };

const CartOverviewStepContainer: FunctionComponent<ContainerProps> = ({
  onSaveCreditCard,
  onChangeStep,
  onUpdatePromoCode,
  promoCodeErrorMessage,
}) => {
  const { addresses, cart, configuration } = useCartContext();
  const { dispatchNotification } = useNotificationsContext();

  const intl = useIntl();
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethods>(
    cart.total.cents > 0 ? "cb" : "free"
  );
  const [saveCreditCard, setSaveCreditCard] = useState<boolean>(false);
  const [savedCreditCard, setSavedCreditCard] = useState<CreditCard | undefined>();
  const [CGUChecked, setCGUChecked] = useState<boolean>(false);
  const [CGUError, setCGUError] = useState<boolean>(false);
  const [displayDiscount, setDisplayDiscount] = useState<boolean>(cart.promoCode !== undefined);
  const [isPaying, setIsPaying] = useState<boolean>(false);
  const [alternatePaymentMethods, setAlternatePaymentMethods] = useState<PaymentMethods[]>([]);

  useEffect(() => {
    setDataLayerTrack(
      "Checkout Step Viewed",
      convertToCheckoutStepProperties("Overview", 3, cart, paymentMethod)
    );
    if (cart.lockedDuringPayment) {
      setIsPaying(true);
      if (configuration.paymentProvider) {
        configuration.paymentProvider.checkPaymentStatus(
          cart.lockedDuringPayment,
          cart.orderNumber,
          handlePaymentError
        );
      }
    }
  }, []);

  const checkMessage = intl.formatMessage({
    id: "cheerz_CartOverviewStep_check",
  });

  useEffect(() => {
    setDisplayDiscount(cart.promoCode !== undefined);
  }, [cart.promoCode]);

  useEffect(() => {
    setPaymentMethod(cart.total.cents > 0 ? "cb" : "free");
  }, [cart.total.cents]);

  useEffect(() => {
    const doIt = () => {
      if (configuration.paymentProvider) {
        configuration.paymentProvider.getGatewayConfigurations(
          addresses.delivery.countryTag2,
          cart.currency,
          setAlternatePaymentMethods
        );
      }
    };
    doIt();
  }, [configuration, setAlternatePaymentMethods]);

  useEffect(() => {
    const doIt = async () => {
      if (configuration.paymentProvider) {
        setSavedCreditCard(await configuration.paymentProvider.getCustomerSavedCard());
      }
    };
    void doIt();
  }, [configuration, setSavedCreditCard]);

  const handleCGUChecked = () => {
    return setCGUChecked(!CGUChecked);
  };

  const availablePayments: PaymentMethods[] = alternatePaymentMethods.concat("cb", "free");

  const handleChangePaymentMethod = (method: PaymentMethods) => {
    setPaymentMethod(method);
  };

  const handleSaveCreditCard = () => {
    setSaveCreditCard(!saveCreditCard);
    onSaveCreditCard();
  };

  const handlePaymentError = (key: string) => {
    const message = intl.formatMessage({
      id: key,
    });
    setIsPaying(false);
    setDataLayerTrack("Payment Error Occured", {
      error: message,
    });
    if (message) {
      dispatchNotification({
        code: "CFE-001",
        message,
        type: "addNotificationByCode",
      });
    } else {
      dispatchNotification({
        code: "CFE-001",
        type: "addNotificationByCode",
      });
    }
  };

  const handleComplete = () => {
    if (CGUChecked === false) {
      setCGUError(true);
      setDataLayerTrack("Payment Without Accepting Terms Clicked");
      return;
    }
    setCGUError(false);
    if (configuration.paymentProvider && paymentMethod) {
      setDataLayerTrack(
        "Checkout Step Completed",
        convertToCheckoutStepProperties("Overview", 3, cart)
      );
      setIsPaying(true);
      configuration.paymentProvider.proceedToPayment(
        cart.orderNumber,
        saveCreditCard,
        paymentMethod,
        handlePaymentError,
        addresses.delivery.countryTag2,
        cart.currency,
        savedCreditCard
      );
    } else {
      dispatchNotification({
        code: "CFV-001",
        type: "addNotificationByCode",
      });
    }
  };

  const changeCreditCard = () => {
    setSavedCreditCard(undefined);
  };

  const handleChangeOrderContent = () => {
    onChangeStep("checkout");
  };

  const handleChangeAddressContent = () => {
    onChangeStep("address");
  };

  return (
    <>
      <CartOverviewStep
        availablePayments={availablePayments}
        billing={addresses.billing}
        CGUChecked={CGUChecked}
        CGUError={CGUError}
        displayDiscount={displayDiscount}
        displayProcessout={true}
        checkMessage={checkMessage}
        isPaying={isPaying}
        onChangeAddressContent={handleChangeAddressContent}
        onChangeCreditCard={changeCreditCard}
        onChangeOrderContent={handleChangeOrderContent}
        onChangePaymentMethod={handleChangePaymentMethod}
        onCheckCGU={handleCGUChecked}
        onCompleteOrder={handleComplete}
        onSaveCreditCard={handleSaveCreditCard}
        onUpdatePromoCode={onUpdatePromoCode}
        promoCodeErrorMessage={promoCodeErrorMessage}
        saveCreditCard={saveCreditCard}
        savedCreditCard={savedCreditCard}
        selectedMethod={paymentMethod}
        total={cart.total}
      />
      <div id="alternative-process-out-container" />
    </>
  );
};

export default CartOverviewStepContainer;
